import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { GALLERY_IMAGES } from './data';
import { useLanguage } from './i18n/LanguageContext';
const SLIDE_INTERVAL = 3400;
const formatIndex = (value: number) => String(value).padStart(2, '0');
export function Gallery() {
  const shouldReduceMotion = useReducedMotion();
  const {
    t
  } = useLanguage();
  const galleryRef = useRef<HTMLElement>(null);
  const isInView = useInView(galleryRef, {
    amount: 0.35
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const gallerySlides = GALLERY_IMAGES.map((image, index) => ({
    image,
    title: t.gallery.slides[index]?.title ?? '',
    detail: t.gallery.slides[index]?.detail ?? ''
  }));
  useEffect(() => {
    if (shouldReduceMotion || !isInView || isPaused) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setDirection(1);
      setActiveIndex(current => (current + 1) % gallerySlides.length);
    }, SLIDE_INTERVAL);
    return () => window.clearTimeout(timer);
  }, [activeIndex, isInView, isPaused, shouldReduceMotion, gallerySlides.length]);
  const selectSlide = (index: number) => {
    setDirection(index > activeIndex ? 1 : -1);
    setActiveIndex(index);
  };
  const showPrevious = () => {
    setDirection(-1);
    setActiveIndex(current => (current - 1 + gallerySlides.length) % gallerySlides.length);
  };
  const showNext = () => {
    setDirection(1);
    setActiveIndex(current => (current + 1) % gallerySlides.length);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      showPrevious();
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      showNext();
    }
  };
  const activeSlide = gallerySlides[activeIndex];
  const slideVariants = {
    enter: (slideDirection: number) => ({
      opacity: 0,
      x: shouldReduceMotion ? 0 : `${slideDirection * 68}%`,
      scale: shouldReduceMotion ? 1 : 1.1
    }),
    center: {
      opacity: 1,
      x: '0%',
      scale: 1
    },
    exit: (slideDirection: number) => ({
      opacity: 0,
      x: shouldReduceMotion ? 0 : `${slideDirection * -34}%`,
      scale: shouldReduceMotion ? 1 : 1.04
    })
  };
  return <motion.section ref={galleryRef} initial={{
    opacity: 0,
    y: shouldReduceMotion ? 0 : 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={{
    duration: shouldReduceMotion ? 0.1 : 0.65,
    ease: [0.22, 1, 0.36, 1]
  }} aria-roledescription="carousel" aria-label={t.gallery.carouselLabel} tabIndex={0} onKeyDown={handleKeyDown} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} onFocus={() => setIsPaused(true)} onBlur={() => setIsPaused(false)} className="relative my-14 overflow-hidden bg-dark text-warm-white outline-none focus-visible:ring-2 focus-visible:ring-champagne focus-visible:ring-offset-4 focus-visible:ring-offset-warm-white" data-id="element-150" data-mp="367i9ghyirtsi">
      <div className="flex items-center justify-between border-b border-champagne/45 px-6 py-5 sm:px-8" data-id="element-151" data-mp="xzdfseg6gr9q">
        <div data-id="element-152" data-mp="1lpg3yjgz1d32">
          <p className="font-body text-[9px] font-semibold uppercase tracking-[4px] text-gold-light sm:text-[10px]" data-id="element-153" data-mp="1loxyqxcbzoj0">
            {t.gallery.eyebrow}
          </p>
          <h2 className="mt-1 font-serif text-2xl font-light sm:text-3xl" data-id="element-154" data-mp="1co9jy0f45k52">{t.gallery.title}</h2>
        </div>
        <span aria-live="polite" className="font-body text-[10px] tracking-[2px] text-cream/60" data-id="element-155" data-mp="1hli76ff4ced6">
          {formatIndex(activeIndex + 1)} / {formatIndex(gallerySlides.length)}
        </span>
      </div>

      <div className="relative aspect-[3/4] overflow-hidden bg-[#35291f] sm:aspect-[3/4]" data-id="element-156" data-mp="685xhnwoh1j3">
        <AnimatePresence initial={false} custom={direction} data-id="element-157" data-mp="li2vtwl0zhr5">
          <motion.img key={activeSlide.image} src={activeSlide.image} alt={`${activeSlide.title}: ${activeSlide.detail}`} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={shouldReduceMotion ? {
          duration: 0.1
        } : {
          x: {
            type: 'spring',
            stiffness: 260,
            damping: 28,
            mass: 0.7
          },
          scale: {
            duration: 0.72,
            ease: [0.22, 1, 0.36, 1]
          },
          opacity: {
            duration: 0.32,
            ease: 'easeOut'
          }
        }} className="absolute inset-0 h-full w-full object-cover object-[center_68%]" data-id="element-158" data-mp="gqb5nn5s7e1j" />
        </AnimatePresence>

        {!shouldReduceMotion && <div aria-hidden="true" className="absolute inset-x-0 top-0 h-[3px] bg-warm-white/10" data-id="element-159" data-mp="3b5bm71r1l283">
            <motion.span key={`${activeIndex}-${isPaused}-${isInView}`} className="block h-full origin-left bg-champagne" initial={{
          scaleX: 0
        }} animate={{
          scaleX: isPaused || !isInView ? 0 : 1
        }} transition={{
          duration: SLIDE_INTERVAL / 1000,
          ease: 'linear'
        }} data-id="element-160" data-mp="1qpdk7k3hdiie" />
          </div>}

        <div className="absolute inset-y-0 left-0 flex items-center px-3 sm:px-5" data-id="element-161" data-mp="29f29awkni4pu">
          <button type="button" onClick={showPrevious} aria-label={t.gallery.previous} className="flex h-10 w-10 items-center justify-center border border-warm-white/70 bg-dark/70 text-warm-white transition-colors hover:border-champagne hover:bg-champagne hover:text-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne sm:h-11 sm:w-11" data-id="element-162" data-mp="2edwrpxephr9f">
            <ChevronLeftIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-163" data-mp="szsef5a91n06" />
          </button>
        </div>
        <div className="absolute inset-y-0 right-0 flex items-center px-3 sm:px-5" data-id="element-164" data-mp="1waoddayysr2h">
          <button type="button" onClick={showNext} aria-label={t.gallery.next} className="flex h-10 w-10 items-center justify-center border border-warm-white/70 bg-dark/70 text-warm-white transition-colors hover:border-champagne hover:bg-champagne hover:text-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne sm:h-11 sm:w-11" data-id="element-165" data-mp="1rcmwmwp0h3u6">
            <ChevronRightIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-166" data-mp="2ac0p1a73e6ik" />
          </button>
        </div>
      </div>

      <div className="flex items-end justify-between bg-[#35291f] px-5 py-4 sm:px-7 sm:py-5" data-id="element-167" data-mp="2oytyvzxuek3d">
        <div data-id="element-168" data-mp="2k23mbml8y6mp">
          <p className="font-serif text-xl sm:text-2xl" data-id="element-169" data-mp="2k1kb0oq3qghn">{activeSlide.title}</p>
          <p className="mt-1 font-body text-[9px] uppercase tracking-[2.5px] text-gold-light sm:text-[10px]" data-id="element-170" data-mp="2y1v29svbpx3c">
            {activeSlide.detail}
          </p>
        </div>
        <span aria-hidden="true" className="mb-1 h-px w-12 bg-champagne/70 sm:w-20" data-id="element-171" data-mp="3cns7cn7psil1" />
      </div>

      <div className="flex items-center justify-between gap-5 px-6 py-5 sm:px-8" data-id="element-172" data-mp="3hmd58fzhuq6b">
        <div className="flex flex-wrap items-center gap-2" aria-label={t.gallery.selectLabel} data-id="element-173" data-mp="3hk92409f5v73">
          {gallerySlides.map((slide, index) => <button key={slide.image} type="button" onClick={() => selectSlide(index)} aria-label={`${t.gallery.viewPrefix} ${slide.title}`} aria-current={index === activeIndex ? 'true' : undefined} className={`h-1.5 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne focus-visible:ring-offset-2 focus-visible:ring-offset-dark ${index === activeIndex ? 'w-8 bg-champagne' : 'w-2 bg-cream/40 hover:bg-champagne/70'}`} data-id="element-174" data-mp="37ne9fxztup0q" data-mpi={index} />)}
        </div>
        <p className="font-body text-[9px] uppercase tracking-[2px] text-cream/60" data-id="element-175" data-mp="2fwq7ijqbxfm0">
          {t.gallery.hint}
        </p>
      </div>
    </motion.section>;
}